import { Fragment, useEffect, useRef, useState, type MouseEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  Link,
  Modal,
  ModalDialog,
  ModalOverflow,
  Sheet,
  Stack,
  Typography,
  Ul,
  UlItem,
} from "@financeable-com-au/financeable-ui";
import { escape as escapeHtml } from "lodash";
// eslint-disable-next-line import/no-unresolved
import { CopyButton } from "@/components/CommonComponents/CopyButton";
// eslint-disable-next-line import/no-unresolved
import { ProgressStepper } from "@/components/CommonComponents/ProgressStepper";
import {
  ERRORS_MESSAGE,
  EMPLOYMENT_INCOME_VERIFICATION_MODAL_COPY,
  EMPLOYMENT_INCOME_VERIFICATION_MODAL_STEPS,
  type EmploymentIncomeVerificationModalStep,
  // eslint-disable-next-line import/no-unresolved
} from "@/constants";
// eslint-disable-next-line import/no-unresolved
import { openSnackbar } from "@/store/slices/snackbarSlice";
import type { UserSlice, UserState } from "@/store/slices/types/userSlice";
// @ts-expect-error: ignoring as it is existing js file
// eslint-disable-next-line import/no-unresolved
import { acceptEmploymentIncomeVerificationTerms, userSelector } from "@/store/slices/userSlice";
import type { AppDispatch } from "@/store/store";
// eslint-disable-next-line import/no-unresolved
import { getErrorToastPayload } from "@/utils/commonFunctions";
import type { IDispatchResponse } from "@/utils/interface";

export interface EmploymentIncomeVerificationModalProps {
  open: boolean;
  onClose: () => void;
  /** The broker has confirmed the customer consent and accepted the terms, so the verification can be ordered. */
  onAgreed: () => void;
}

/** Plain text with the documents it names in place, as links. */
type LinkedText = readonly (string | { label: string; href: string })[];

const modalWidth = "560px";

/** Only an open modal renders its content, so one id serves the modal of every customer on the page. */
const TITLE_ID = "employment-income-verification-modal-title";

const { TERMS, CONSENT } = EMPLOYMENT_INCOME_VERIFICATION_MODAL_STEPS;
const { cancel, terms, consent } = EMPLOYMENT_INCOME_VERIFICATION_MODAL_COPY;
const { statement } = consent;

/** Pasted into a Word or Google document, each document named keeps its link. */
const toHtml = (text: LinkedText) =>
  text
    .map((part) =>
      typeof part === "string" ? escapeHtml(part) : `<a href="${escapeHtml(part.href)}">${escapeHtml(part.label)}</a>`,
    )
    .join("");

/** Pasted as plain text, each document named is followed by its address. */
const toPlainText = (text: LinkedText) =>
  text.map((part) => (typeof part === "string" ? part : `${part.label} (${part.href})`)).join("");

/** The statement as the broker copies it into their Privacy Consent Agreement, laid out as it is shown. */
const STATEMENT_HTML = [
  `<p>${escapeHtml(statement.intro)}</p>`,
  `<ul>${statement.items.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul>`,
  `<p>${toHtml(statement.closing)}</p>`,
].join("");

const STATEMENT_TEXT = [
  statement.intro,
  statement.items.map((item) => `• ${item}`).join("\n"),
  toPlainText(statement.closing),
].join("\n\n");

/** The documents open in a new tab, so the broker keeps their place in the modal. */
const LinkedParagraph = ({ text }: { text: LinkedText }) => (
  <Typography>
    {text.map((part, index) => (
      <Fragment key={index}>
        {typeof part === "string" ? (
          part
        ) : (
          <Link href={part.href} target="_blank" rel="noopener noreferrer">
            {part.label}
          </Link>
        )}
      </Fragment>
    ))}
  </Typography>
);

const TermsStep = () => (
  <Stack rowGap={3}>
    <Typography component="h2" id={TITLE_ID} level="title-md">
      {terms.title}
    </Typography>
    {terms.paragraphs.map((paragraph, index) => (
      <LinkedParagraph key={index} text={paragraph} />
    ))}
  </Stack>
);

interface ConsentStepProps {
  isPrivacyConsentConfirmed: boolean;
  onPrivacyConsentConfirmedChange: (isConfirmed: boolean) => void;
}

const ConsentStep = ({ isPrivacyConsentConfirmed, onPrivacyConsentConfirmedChange }: ConsentStepProps) => (
  <Stack rowGap={3}>
    <Typography component="h2" id={TITLE_ID} level="title-md">
      {consent.title}
    </Typography>
    <Typography>{consent.intro}</Typography>
    {/* Set apart, so the broker sees exactly what to add to their Privacy Consent Agreement. */}
    <Sheet variant="outlined" borderRadius="md" padding={4}>
      <Stack rowGap={2}>
        {/* A plain block, so the statement wraps around the floated button; it would not in the flex column. */}
        <Box>
          {/* Raised a little, level with the first line of the statement. */}
          <Box sx={{ float: "right", marginLeft: 2, marginTop: -1 }}>
            <CopyButton text={STATEMENT_TEXT} html={STATEMENT_HTML} aria-label={consent.copyStatement} />
          </Box>
          <Typography>{statement.intro}</Typography>
        </Box>
        <Ul>
          {statement.items.map((item) => (
            <UlItem key={item}>{item}</UlItem>
          ))}
        </Ul>
        <LinkedParagraph text={statement.closing} />
      </Stack>
    </Sheet>
    <Checkbox
      label={consent.privacyConsentConfirmation}
      size="sm"
      checked={isPrivacyConsentConfirmed}
      onChange={(event) => onPrivacyConsentConfirmedChange(event.target.checked)}
    />
  </Stack>
);

/** Mounted each time the modal opens, so a broker who stopped at the terms last time starts again from the consent. */
const EmploymentIncomeVerificationSteps = ({
  onClose,
  onAgreed,
}: Omit<EmploymentIncomeVerificationModalProps, "open">) => {
  const dispatch = useDispatch<AppDispatch>();
  const { acceptingEmploymentIncomeVerificationTerms } = useSelector<UserSlice, UserState>(userSelector);
  const [step, setStep] = useState<EmploymentIncomeVerificationModalStep>(CONSENT);
  const [isPrivacyConsentConfirmed, setIsPrivacyConsentConfirmed] = useState(false);
  const isMountedRef = useRef(true);
  const isConsentStep = step === CONSENT;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /** The consent and the terms are saved together, in one call, once the terms are accepted. */
  const handleAcceptTerms = async (event: MouseEvent<HTMLButtonElement>) => {
    // The second click of a double-click on Confirm lands here; only a click of its own orders the paid check.
    if (event.detail > 1) return;

    const res: IDispatchResponse = await dispatch(acceptEmploymentIncomeVerificationTerms());

    if (res?.error) {
      dispatch(openSnackbar(getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)));
      return;
    }

    // A broker who closed the modal while the acceptance was saved has not asked for the paid check.
    if (isMountedRef.current) onAgreed();
  };

  return (
    <Stack rowGap={6}>
      <ProgressStepper
        activeStep={step}
        steps={[
          { key: CONSENT, label: consent.stepLabel, isCompleted: !isConsentStep },
          { key: TERMS, label: terms.stepLabel },
        ]}
      />

      {isConsentStep ? (
        <ConsentStep
          isPrivacyConsentConfirmed={isPrivacyConsentConfirmed}
          onPrivacyConsentConfirmedChange={setIsPrivacyConsentConfirmed}
        />
      ) : (
        <TermsStep />
      )}

      <Stack direction="row" columnGap={2}>
        <Button variant="secondary" fullWidth onClick={onClose}>
          {cancel}
        </Button>
        {/* Keyed by step, so the focused Confirm button does not turn into the paid Accept button in place. */}
        {isConsentStep ? (
          <Button
            key={CONSENT}
            variant="primary"
            fullWidth
            disabled={!isPrivacyConsentConfirmed}
            onClick={() => setStep(TERMS)}
          >
            {consent.confirm}
          </Button>
        ) : (
          <Button
            key={TERMS}
            variant="primary"
            fullWidth
            loading={acceptingEmploymentIncomeVerificationTerms}
            onClick={handleAcceptTerms}
          >
            {terms.accept}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

/**
 * Asked once, before the broker's first employment and income verification: they confirm the customer consent, then accept Equifax's
 * service terms, and both are saved on their user together.
 */
const EmploymentIncomeVerificationModal = ({ open, onClose, onAgreed }: EmploymentIncomeVerificationModalProps) => (
  <Modal open={open} onClose={onClose}>
    <ModalOverflow>
      <ModalDialog aria-labelledby={TITLE_ID} minWidth={modalWidth} maxWidth={modalWidth}>
        <EmploymentIncomeVerificationSteps onClose={onClose} onAgreed={onAgreed} />
      </ModalDialog>
    </ModalOverflow>
  </Modal>
);

export { EmploymentIncomeVerificationModal };
